import { Mixpanel } from 'mixpanel-react-native';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { getUtmEventProperties } from '@utils/utmParams';

const IS_PRODUCTION = process.env.APP_ENV === 'production';

interface MixpanelContextType {
  mixpanel: Mixpanel | null;
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
}

const MixpanelContext = createContext<MixpanelContextType | undefined>(
  undefined
);

export const MixpanelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mixpanelInstance, setMixpanelInstance] = useState<Mixpanel | null>(
    null
  );
  // Buffer events that fire before the SDK finishes async init (capped to avoid unbounded growth)
  const MAX_QUEUE = 50;
  const eventQueue = useRef<
    Array<{ name: string; properties?: Record<string, any> }>
  >([]);

  useEffect(() => {
    const initMixpanel = async () => {
      if (!IS_PRODUCTION) return;

      const token = process.env.MIXPANEL_TOKEN;
      if (!token) {
        console.warn(
          'MIXPANEL_TOKEN environment variable is not set. Mixpanel will not be initialized.'
        );
        return;
      }

      const trackAutomaticEvents = true; //disable legacy mobile autotrack
      const useNative = false; //disable Native Mode, use Javascript Mode

      const mp = new Mixpanel(token, trackAutomaticEvents, useNative);
      await mp.init();
      // Set platform once at init so it appears on every event automatically,
      // including pre-auth events (user_signed_up, user_logged_in) that fire
      // before MixpanelGlobalProperties registers super properties.
      mp.registerSuperProperties({
        platform: Platform.OS,
        device_type: Platform.OS,
      });
      // Register first-touch UTM params as super properties so they attach
      // automatically to every event, including ones buffered before init.
      // All 5 UTM keys are always registered — absent keys get the string
      // 'undefined' so Mixpanel retains them as properties on every event
      // (satisfying the AC: no-UTM signups show utm_source = 'undefined').
      if (Platform.OS === 'web') {
        mp.registerSuperProperties(getUtmEventProperties());
      }
      // Set the instance first so any event that arrives during the flush
      // is routed directly to mp rather than re-queued.
      setMixpanelInstance(mp);
      // Flush buffered events (uses mp directly; state update is async)
      eventQueue.current.forEach(({ name, properties }) =>
        mp.track(name, properties)
      );
      eventQueue.current = [];
    };

    initMixpanel();
  }, []);

  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      if (mixpanelInstance) {
        mixpanelInstance.track(eventName, properties);
      } else if (IS_PRODUCTION && eventQueue.current.length < MAX_QUEUE) {
        // SDK still initializing — queue the event for replay once ready.
        // Only queue in production; in other environments Mixpanel never inits
        // so queued events would never flush (wasted memory).
        eventQueue.current.push({ name: eventName, properties });
      }
    },
    [mixpanelInstance]
  );

  const memoizedValue = useMemo(
    () => ({ mixpanel: mixpanelInstance, trackEvent }),
    [mixpanelInstance, trackEvent]
  );

  return (
    <MixpanelContext.Provider value={memoizedValue}>
      {children}
    </MixpanelContext.Provider>
  );
};

export const useMixpanel = () => {
  const context = useContext(MixpanelContext);
  if (context === undefined) {
    throw new Error('useMixpanel must be used within a MixpanelProvider');
  }
  return context;
};
